import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DomioLogo from '../components/common/DomioLogo';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useI18n } from '../i18n';
import {
  Building2, Users, DollarSign, Wrench, Shield, Bell, BarChart3,
  FileText, Globe, Smartphone, ArrowRight, Check, ChevronDown,
  Menu, X, Sparkles, CalendarDays, Mail,
} from 'lucide-react';

const FEATURE_CONFIG = [
  { icon: Building2, titleKey: 'landing.features.portfolio.title', descKey: 'landing.features.portfolio.desc' },
  { icon: Users, titleKey: 'landing.features.tenantPortal.title', descKey: 'landing.features.tenantPortal.desc' },
  { icon: DollarSign, titleKey: 'landing.features.financial.title', descKey: 'landing.features.financial.desc' },
  { icon: Wrench, titleKey: 'landing.features.maintenance.title', descKey: 'landing.features.maintenance.desc' },
  { icon: FileText, titleKey: 'landing.features.aiDocs.title', descKey: 'landing.features.aiDocs.desc' },
  { icon: Bell, titleKey: 'landing.features.notifications.title', descKey: 'landing.features.notifications.desc' },
  { icon: CalendarDays, titleKey: 'landing.features.inspections.title', descKey: 'landing.features.inspections.desc' },
  { icon: Shield, titleKey: 'landing.features.security.title', descKey: 'landing.features.security.desc' },
  { icon: Globe, titleKey: 'landing.features.i18n.title', descKey: 'landing.features.i18n.desc' },
];

const SOLUTION_CONFIG = [
  {
    key: 'owners',
    items: ['item1', 'item2', 'item3', 'item4'],
  },
  {
    key: 'managers',
    items: ['item1', 'item2', 'item3', 'item4'],
  },
  {
    key: 'tenants',
    items: ['item1', 'item2', 'item3', 'item4'],
  },
];

const LandingPage = () => {
  const { t } = useI18n();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navLinks = useMemo(
    () => [
      { label: t('landing.nav.features'), href: '/#features' },
      { label: t('landing.nav.solutions'), href: '/#solutions' },
      { label: t('landing.nav.about'), href: '/#about' },
      { label: t('landing.nav.pricing'), href: '/pricing' },
      { label: t('landing.nav.contact'), href: '/#contact' },
    ],
    [t],
  );

  const heroStats = useMemo(
    () => [
      { icon: Building2, label: t('landing.hero.statProperties'), val: '12', color: 'text-domio-green' },
      { icon: Users, label: t('landing.hero.statTenants'), val: '28', color: 'text-blue-400' },
      { icon: DollarSign, label: t('landing.hero.statRevenue'), val: '$47.2K', color: 'text-emerald-400' },
      { icon: Wrench, label: t('landing.hero.statRequests'), val: '3', color: 'text-amber-400' },
    ],
    [t],
  );

  const platformItems = useMemo(
    () => [
      { icon: BarChart3, label: t('landing.platform.desktop') },
      { icon: Smartphone, label: t('landing.platform.mobile') },
      { icon: Globe, label: t('landing.platform.browser') },
    ],
    [t],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-figtree">
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <DomioLogo
            size="md"
            showText
            textClassName={scrolled ? 'text-slate-900' : 'text-white'}
            trailing={
              <LanguageSwitcher
                dark={!scrolled}
                inline={scrolled}
                className="shrink-0 hidden sm:flex"
              />
            }
          />
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((l) =>
              l.href.startsWith('/#') ? (
                <a
                  key={l.href}
                  href={l.href}
                  className={`text-sm font-medium transition-colors hover:text-domio-green ${scrolled ? 'text-slate-700' : 'text-white/80'}`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  className={`text-sm font-medium transition-colors hover:text-domio-green ${scrolled ? 'text-slate-700' : 'text-white/80'}`}
                >
                  {l.label}
                </Link>
              ),
            )}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className={`text-sm font-medium px-4 py-2 rounded-lg transition ${scrolled ? 'text-slate-700 hover:text-domio-600' : 'text-white hover:text-domio-green'}`}
            >
              {t('landing.nav.logIn')}
            </Link>
            <Link
              to="/register"
              className="text-sm font-bold px-5 py-2.5 bg-domio-green text-white rounded-lg hover:bg-domio-green-dark transition shadow-lg shadow-domio-green/25"
            >
              {t('landing.nav.getStarted')}
            </Link>
          </div>
          <button type="button" className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? (
              <X className={`h-6 w-6 ${scrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${scrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t shadow-lg p-4 space-y-3">
            {navLinks.map((l) =>
              l.href.startsWith('/#') ? (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileMenu(false)}
                  className="block text-slate-700 font-medium py-2"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setMobileMenu(false)}
                  className="block text-slate-700 font-medium py-2"
                >
                  {l.label}
                </Link>
              ),
            )}
            <div className="py-1">
              <LanguageSwitcher inline />
            </div>
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="flex-1 text-center text-sm font-medium py-2.5 border border-slate-300 rounded-lg">
                {t('landing.nav.logIn')}
              </Link>
              <Link to="/register" className="flex-1 text-center text-sm font-bold py-2.5 bg-domio-green text-white rounded-lg">
                {t('landing.nav.getStarted')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <section className="relative min-h-[90vh] flex items-center bg-[#071E35] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#071E35] via-[#0A2F52] to-[#0D3E6B]" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 50%, #73B83F 0%, transparent 50%), radial-gradient(circle at 75% 50%, #0D3E6B 0%, transparent 50%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-sm">
              <Sparkles className="h-4 w-4 text-domio-green" /> {t('landing.hero.badge')}
            </div>
            <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              {t('landing.hero.titleLine1')}
              <br />
              <span className="text-domio-green">{t('landing.hero.titleLine2')}</span>
              <br />
              {t('landing.hero.titleLine3')}
            </h1>
            <p className="text-lg text-slate-300 max-w-lg leading-relaxed">{t('landing.hero.subtitle')}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-domio-green text-white font-bold rounded-lg hover:bg-domio-green-dark transition shadow-xl shadow-domio-green/30 text-base"
              >
                {t('landing.hero.startFree')} <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition backdrop-blur-sm border border-white/20 text-base"
              >
                {t('landing.hero.exploreFeatures')} <ChevronDown className="h-5 w-5" />
              </a>
            </div>
            <div className="flex items-center gap-6 pt-4 text-sm text-slate-400 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-domio-green" /> {t('landing.hero.freeToStart')}
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-domio-green" /> {t('landing.hero.noCreditCard')}
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-domio-green" /> {t('landing.hero.cancelAnytime')}
              </span>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6 shadow-2xl">
              <div className="grid grid-cols-2 gap-4">
                {heroStats.map((s) => (
                  <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <s.icon className={`h-5 w-5 ${s.color} mb-2`} />
                    <p className="text-2xl font-bold text-white">{s.val}</p>
                    <p className="text-xs text-slate-400">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-domio-green/20 rounded-lg p-3 border border-domio-green/30 flex items-center gap-3">
                <Bell className="h-5 w-5 text-domio-green" />
                <div>
                  <p className="text-sm text-white font-medium">{t('landing.hero.notificationTitle')}</p>
                  <p className="text-xs text-slate-400">{t('landing.hero.notificationBody')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">{t('landing.features.eyebrow')}</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900">{t('landing.features.title')}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">{t('landing.features.subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURE_CONFIG.map((f) => (
              <div
                key={f.titleKey}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-domio-green/40 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-lg bg-domio-50 flex items-center justify-center mb-4 group-hover:bg-domio-green/10 transition-colors">
                  <f.icon className="h-6 w-6 text-domio-600" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-slate-900 mb-2">{t(f.titleKey)}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="solutions" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">{t('landing.solutions.eyebrow')}</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900">{t('landing.solutions.title')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {SOLUTION_CONFIG.map((s) => (
              <div key={s.key} className="bg-slate-50 p-8 rounded-xl border border-slate-200">
                <h3 className="font-manrope font-bold text-xl text-slate-900 mb-3">
                  {t(`landing.solutions.${s.key}.title`)}
                </h3>
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">{t(`landing.solutions.${s.key}.desc`)}</p>
                <ul className="space-y-2.5">
                  {s.items.map((itemKey) => (
                    <li key={itemKey} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-domio-green flex-shrink-0" />
                      {t(`landing.solutions.${s.key}.${itemKey}`)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#071E35]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">{t('landing.platform.eyebrow')}</p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mb-6">{t('landing.platform.title')}</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-12">{t('landing.platform.subtitle')}</p>
          <div className="flex justify-center gap-12 flex-wrap">
            {platformItems.map((p) => (
              <div key={p.label} className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
                  <p.icon className="h-8 w-8 text-domio-green" />
                </div>
                <p className="text-white font-medium">{p.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">{t('landing.about.eyebrow')}</p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 mb-6">{t('landing.about.title')}</h2>
          <p className="text-slate-500 text-lg leading-relaxed">{t('landing.about.body')}</p>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-domio-600 to-[#071E35]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mb-6">{t('landing.cta.title')}</h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">{t('landing.cta.subtitle')}</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-domio-green text-white font-bold rounded-lg hover:bg-domio-green-dark transition shadow-xl text-lg"
            >
              {t('landing.cta.button')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">{t('landing.contact.eyebrow')}</p>
          <h2 className="font-manrope text-3xl font-bold text-slate-900 mb-4">{t('landing.contact.title')}</h2>
          <p className="text-slate-500 mb-8">{t('landing.contact.subtitle')}</p>
          <a
            href="mailto:info@domio.world"
            className="inline-flex items-center gap-2 text-domio-600 font-semibold text-lg hover:text-domio-green transition"
          >
            <Mail className="h-5 w-5" /> info@domio.world
          </a>
        </div>
      </section>

      <footer className="bg-[#071E35] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <DomioLogo size="md" showText textClassName="text-white" />
            <div className="flex gap-6 text-sm text-slate-400 flex-wrap justify-center">
              {navLinks.map((l) =>
                l.href.startsWith('/#') ? (
                  <a key={l.href} href={l.href} className="hover:text-white transition">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.href} to={l.href} className="hover:text-white transition">
                    {l.label}
                  </Link>
                ),
              )}
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} Domio. {t('landing.footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
